import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { Suspense, lazy } from "react";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import NewsletterPopup from "@/components/NewsletterPopup";
import {
  HomePageSkeleton,
  ListPageSkeleton,
  DetailPageSkeleton,
  TablePageSkeleton,
  DefaultPageSkeleton,
} from "@/components/skeletons/PageSkeleton";

// Critical pages loaded immediately
import Index from "./pages/Index";
// Lazy load all other pages for better performance
const BrowseFirms = lazy(() => import("./pages/BrowseFirms"));
const Compare = lazy(() => import("./pages/Compare"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const Auth = lazy(() => import("./pages/Auth"));
const TraderDashboardHome = lazy(() => import("./pages/TraderDashboardHome"));
const Admin = lazy(() => import("./pages/Admin"));
const FirmPanel = lazy(() => import("./pages/FirmPanel"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DynamicFirm = lazy(() => import("./pages/DynamicFirm"));

const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Offers = lazy(() => import("./pages/Offers"));

const Payouts = lazy(() => import("./pages/Payouts"));
const Cashback = lazy(() => import("./pages/Cashback"));
const Journal = lazy(() => import("./pages/Journal"));
const DynamicCoupon = lazy(() => import("./pages/DynamicCoupon"));
const BestEvaluationFirms = lazy(() => import("./pages/category/BestEvaluationFirms"));
const Complaints = lazy(() => import("./pages/Complaints"));
const Newsletter = lazy(() => import("./pages/Newsletter"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <div className="relative min-h-screen bg-[#0E141B] text-[#F2F2F2] flex flex-col">
            {/* This new div creates the background glow effect */}
            <div className="absolute top-0 left-0 -z-10 h-96 w-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-electric-blue/20 to-transparent"></div>

            <Navbar />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<Suspense fallback={<HomePageSkeleton />}><Index /></Suspense>} />
                <Route path="/browsefirms" element={<Suspense fallback={<ListPageSkeleton />}><BrowseFirms /></Suspense>} />
                <Route path="/compare" element={<Suspense fallback={<TablePageSkeleton />}><Compare /></Suspense>} />

                <Route path="/about" element={<Suspense fallback={<DefaultPageSkeleton />}><AboutUs /></Suspense>} />
                <Route path="/contact" element={<Suspense fallback={<DefaultPageSkeleton />}><ContactUs /></Suspense>} />
                <Route path="/privacy" element={<Suspense fallback={<DefaultPageSkeleton />}><PrivacyPolicy /></Suspense>} />
                <Route path="/terms" element={<Suspense fallback={<DefaultPageSkeleton />}><TermsAndConditions /></Suspense>} />
                <Route path="/auth" element={<Suspense fallback={<DefaultPageSkeleton />}><Auth /></Suspense>} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<TablePageSkeleton />}><TraderDashboardHome /></Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route path="/journal" element={<Suspense fallback={<TablePageSkeleton />}><Journal /></Suspense>} />
                {/* Admin panel — obscured URL (defense-in-depth; real protection is role check + RLS) */}
                <Route
                  path="/sys-x9k2m-panel-7q4w/admin"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <Suspense fallback={<TablePageSkeleton />}><Admin /></Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/firm-panel"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<TablePageSkeleton />}><FirmPanel /></Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route path="/accept-invite/:token" element={<Suspense fallback={<DefaultPageSkeleton />}><AcceptInvite /></Suspense>} />
                {/* Dynamic firm pages — all firm data is loaded from Supabase */}
                <Route path="/firms/:slug" element={<Suspense fallback={<DetailPageSkeleton />}><DynamicFirm /></Suspense>} />
                <Route path="/firm/:slug" element={<Suspense fallback={<DetailPageSkeleton />}><DynamicFirm /></Suspense>} />
                {/* Legacy hardcoded firm URL aliases — redirect to dynamic page */}
                <Route path="/atlas-funded" element={<Navigate to="/firm/atlas-funded" replace />} />
                <Route path="/blueberry-funded" element={<Navigate to="/firm/blueberry-funded" replace />} />
                <Route path="/klein-funding-crypto" element={<Navigate to="/firm/klein-funding-crypto" replace />} />
                <Route path="/blue-guardian" element={<Navigate to="/firm/blue-guardian" replace />} />
                <Route path="/ats-funded" element={<Navigate to="/firm/ats-funded" replace />} />
                <Route path="/alpha-capital" element={<Navigate to="/firm/alpha-capital" replace />} />
                <Route path="/aqua-funded" element={<Navigate to="/firm/aqua-funded" replace />} />
                <Route path="/qt-funded" element={<Navigate to="/firm/qt-funded" replace />} />
                <Route path="/finotive-funding" element={<Navigate to="/firm/finotive-funding" replace />} />
                <Route path="/city-traders-imperium" element={<Navigate to="/firm/city-traders-imperium" replace />} />
                <Route path="/think-capital" element={<Navigate to="/firm/think-capital" replace />} />
                <Route path="/fundingpips" element={<Navigate to="/firm/fundingpips" replace />} />
                <Route path="/nostro" element={<Navigate to="/firm/nostro" replace />} />
                <Route path="/goat-funded-trader" element={<Navigate to="/firm/goat-funded-trader" replace />} />
                <Route path="/maven" element={<Navigate to="/firm/maven" replace />} />
                <Route path="/seacrest-markets" element={<Navigate to="/firm/seacrest-markets" replace />} />
                <Route path="/funded-trader-markets" element={<Navigate to="/firm/funded-trader-markets" replace />} />
                <Route path="/payouts" element={<Suspense fallback={<ListPageSkeleton />}><Payouts /></Suspense>} />

                {/* Blog Routes — all posts driven by DB, fully editable in /admin */}
                <Route path="/blog" element={<Suspense fallback={<ListPageSkeleton />}><Blog /></Suspense>} />
                <Route path="/blog/:slug" element={<Suspense fallback={<DetailPageSkeleton />}><BlogPost /></Suspense>} />

                <Route path="/offers" element={<Suspense fallback={<ListPageSkeleton />}><Offers /></Suspense>} />
                <Route path="/cashback" element={<Suspense fallback={<ListPageSkeleton />}><Cashback /></Suspense>} />
                {/* Klein has two coupon code aliases — keep both pointing at klein-funding-crypto */}
                <Route path="/klein-funding-coupon-code" element={<Navigate to="/klein-funding-crypto-coupon-code" replace />} />
                <Route path="/funding-pips-coupon-code" element={<Navigate to="/fundingpips-coupon-code" replace />} />
                <Route path="/best-evaluation-firms" element={<Suspense fallback={<ListPageSkeleton />}><BestEvaluationFirms /></Suspense>} />
                <Route path="/complaints" element={<Suspense fallback={<ListPageSkeleton />}><Complaints /></Suspense>} />
                <Route path="/newsletter" element={<Suspense fallback={<DefaultPageSkeleton />}><Newsletter /></Suspense>} />
                {/* Dynamic coupon page — matches any /:slug-coupon-code URL; component validates slug. Must be last before catch-all. */}
                <Route path="/:couponSlug" element={<Suspense fallback={<DetailPageSkeleton />}><DynamicCoupon /></Suspense>} />
                <Route path="*" element={<Suspense fallback={<DefaultPageSkeleton />}><NotFound /></Suspense>} />
              </Routes>
            </main>
            <Footer />
            <CookieConsent />
            <NewsletterPopup />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
